import { useEffect, useMemo, useState } from "react";
import {
  User, Sparkles, Cog, HardHat, Headset, ClipboardList, Landmark,
  ChevronLeft, ChevronRight, Check, X, ArrowRight, Play, Pause, Languages, Layers, LifeBuoy,
} from "lucide-react";
import { ACTORS, ZONES, FLOWS, FLOW_GROUPS, NOT_IN_MVP } from "@/data/fieldServicesFlow";

const ICONS = { User, Sparkles, Cog, HardHat, Headset, ClipboardList, Landmark, LifeBuoy };

const TONE = {
  sand:    { ring: "#B9AC9B", chip: "rgba(185,172,155,.14)" },
  red:     { ring: "#E44E4E", chip: "rgba(228,78,78,.14)" },
  slate:   { ring: "#7C8A99", chip: "rgba(124,138,153,.14)" },
  amber:   { ring: "#D9A13B", chip: "rgba(217,161,59,.16)" },
  indigo:  { ring: "#7C83E8", chip: "rgba(124,131,232,.16)" },
  emerald: { ring: "#3FA46A", chip: "rgba(63,164,106,.16)" },
  cyan:    { ring: "#5AA9C9", chip: "rgba(90,169,201,.16)" },
};

const CH = {
  MMS: "MMS", SMS: "SMS", email: "EMAIL", "in-app": "IN-APP", voice: "VOICE",
};

function ActorNode({ id, active, dim }) {
  const a = ACTORS[id];
  const Icon = ICONS[a.icon];
  const t = TONE[a.tone];
  return (
    <div
      data-testid={`actor-${id}`}
      className="flex flex-col items-center gap-2 transition-all duration-500"
      style={{ opacity: dim ? 0.28 : 1, transform: active ? "translateY(-3px)" : "none" }}
    >
      <div
        className="relative h-12 w-12 rounded-full grid place-items-center transition-all duration-500"
        style={{
          background: active ? t.chip : "rgba(255,255,255,.04)",
          boxShadow: active ? `0 0 0 1.5px ${t.ring}, 0 0 26px -4px ${t.ring}` : "0 0 0 1px rgba(255,255,255,.09)",
        }}
      >
        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} style={{ color: active ? t.ring : "#8C8073" }} />
        {active && (
          <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full animate-pulse" style={{ background: t.ring }} />
        )}
      </div>
      <div className="text-center leading-tight">
        <div className="text-[11px] font-semibold tracking-tight" style={{ color: active ? "#FAF8F5" : "#8C8073" }}>{a.label}</div>
        <div className="text-[9.5px] text-[#6b635c] max-w-[86px]">{a.sub}</div>
      </div>
    </div>
  );
}

function Bubble({ m, lang }) {
  const a = ACTORS[m.to];
  const t = TONE[a?.tone || "slate"];
  const inbound = m.kind === "in";
  const body = (lang === "es" && m.body.es) ? m.body.es : (m.body.en || m.body.es);
  const showGloss = lang === "es" && m.body.es && m.body.en;
  return (
    <div className="animate-[fade-up_.5s_cubic-bezier(.16,1,.3,1)_forwards] opacity-0" style={{ animationDelay: `${m._i * 90}ms` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[9px] font-bold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded"
              style={{ background: t.chip, color: t.ring }}>{CH[m.ch] || m.ch}</span>
        <span className="text-[10.5px] text-[#8C8073] flex items-center gap-1">
          {inbound ? <>{a.label} <ArrowRight className="h-3 w-3" /> platform</> : <>platform <ArrowRight className="h-3 w-3" /> {a.label}</>}
        </span>
      </div>
      <div className="rounded-[14px] px-3.5 py-2.5 text-[12.5px] leading-relaxed whitespace-pre-line"
           style={{
             background: inbound ? "rgba(255,255,255,.035)" : "rgba(255,255,255,.06)",
             borderLeft: `2px solid ${t.ring}`,
             color: "#EDE7DF",
           }}>
        {body}
        {showGloss && (
          <div className="mt-2 pt-2 text-[11px] text-[#8C8073] italic" style={{ borderTop: "1px dashed rgba(255,255,255,.12)" }}>
            manager reads: {m.body.en}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FieldServicesFlow() {
  const [flowId, setFlowId] = useState("spine");
  const [i, setI] = useState(0);
  const [lang, setLang] = useState("es");
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState("flow");

  const flow = useMemo(() => FLOWS.find((f) => f.id === flowId), [flowId]);
  // Clamp at read time: switching to a shorter flow used to leave `i` past the end and kill the page.
  const cur = Math.min(i, flow.steps.length - 1);
  const step = flow.steps[cur];
  const last = cur >= flow.steps.length - 1;

  useEffect(() => { setI(0); setPlaying(false); }, [flowId]);

  useEffect(() => {
    if (!playing) return;
    if (last) { setPlaying(false); return; }
    const id = setTimeout(() => setI((v) => v + 1), 4200);
    return () => clearTimeout(id);
  }, [playing, i, last]);

  const activeActors = useMemo(() => {
    const s = new Set([step.by]);
    (step.msgs || []).forEach((m) => s.add(m.to));
    return s;
  }, [step]);

  const msgs = (step.msgs || []).map((m, idx) => ({ ...m, _i: idx }));

  return (
    <div className="min-h-screen font-body" style={{ background: "#141110", color: "#EDE7DF" }} data-testid="fs-flow-page">
      {/* grain */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.035] z-0"
           style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      <div className="relative z-10 max-w-[1180px] mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* header */}
        <div className="flex flex-wrap items-end justify-between gap-4 pb-6" style={{ borderBottom: "1px solid rgba(255,255,255,.1)" }}>
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.24em] text-[#E44E4E] mb-2">Ask Redline · Field Services</div>
            <h1 className="font-heading text-3xl sm:text-[2.6rem] font-extrabold tracking-tight leading-[1.05]">
              How this works.
            </h1>
            <p className="text-[13.5px] text-[#A69C90] mt-2 max-w-[52ch]">
              Fifteen real situations, click by click — including the exact text each person receives.
              Start with <b className="text-[#DED6CC]">Where every commitment lives</b>; the rest make more sense after it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="tab-flow-btn" onClick={() => setTab("flow")}
              className="rounded-full px-4 py-2 text-[12px] font-semibold transition-colors"
              style={tab === "flow" ? { background: "#D32F2F", color: "#fff" } : { background: "rgba(255,255,255,.06)", color: "#A69C90" }}>
              Flows
            </button>
            <button data-testid="tab-map-btn" onClick={() => setTab("map")}
              className="rounded-full px-4 py-2 text-[12px] font-semibold transition-colors inline-flex items-center gap-1.5"
              style={tab === "map" ? { background: "#D32F2F", color: "#fff" } : { background: "rgba(255,255,255,.06)", color: "#A69C90" }}>
              <Layers className="h-3.5 w-3.5" /> System map
            </button>
          </div>
        </div>

        {tab === "map" ? (
          <div className="pt-10">
            <div className="grid md:grid-cols-2 gap-5">
              {ZONES.map((z, idx) => (
                <div key={z.id} data-testid={`zone-${z.id}`}
                     className="rounded-2xl p-5 animate-[fade-up_.6s_cubic-bezier(.16,1,.3,1)_forwards] opacity-0"
                     style={{ background: "rgba(255,255,255,.035)", boxShadow: "0 0 0 1px rgba(255,255,255,.08)", animationDelay: `${idx * 90}ms` }}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E44E4E] mb-1">{z.label}</div>
                  <div className="text-[12px] text-[#8C8073] mb-4">{z.sub}</div>
                  <ul className="space-y-2">
                    {z.items.map((it) => (
                      <li key={it} className="flex gap-2.5 text-[13px] leading-relaxed text-[#DED6CC]">
                        <span className="mt-[7px] h-1 w-1 rounded-full bg-[#B9AC9B] shrink-0" />{it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl p-6" style={{ background: "rgba(228,78,78,.06)", boxShadow: "inset 3px 0 0 #D32F2F" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#E44E4E] mb-3">The two rules everything else follows from</div>
              <p className="text-[14px] leading-relaxed text-[#EDE7DF] max-w-[70ch]">
                <strong>One.</strong> The notification goes to <strong>the person standing in the building</strong>. The
                vendor's office is copied for visibility and only becomes responsible if that person doesn't answer in
                ten minutes. We know who's on site because the vendor told us once, per property, per trade — and can
                change it in one tap. Nobody guesses, and no job can die quietly.
              </p>
              <p className="text-[14px] leading-relaxed text-[#EDE7DF] max-w-[70ch] mt-3">
                <strong>Two.</strong> Insurance gates <strong>work, not access</strong>. Registering, hearing about a
                job, asking a question and walking the property need no paperwork at all. Bidding, being handed a job
                and invoicing need a current certificate, a W-9 and a signed agreement — and a certificate that lapses
                mid-job pauses live work, with no emergency override.
              </p>
            </div>

            <div className="mt-8">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8C8073] mb-3">Not built — on purpose, or not yet</div>
              <div className="flex flex-wrap gap-2">
                {NOT_IN_MVP.map((n) => (
                  <span key={n} className="text-[11.5px] px-3 py-1.5 rounded-full text-[#A69C90]" style={{ background: "rgba(255,255,255,.045)" }}>{n}</span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* flow picker, grouped — fifteen flat buttons is a wall */}
            <div className="space-y-4 pt-6">
              {FLOW_GROUPS.map((g) => {
                const items = g.ids.map((id) => FLOWS.find((f) => f.id === id)).filter(Boolean);
                if (!items.length) return null;
                return (
                  <div key={g.label}>
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.2em] text-[#6b635c] mb-2">{g.label}</div>
                    <div className="flex flex-wrap gap-2">
                      {items.map((f) => (
                        <button key={f.id} data-testid={`flow-btn-${f.id}`} onClick={() => setFlowId(f.id)}
                          className="text-left rounded-xl px-4 py-2.5 transition-all duration-300"
                          style={f.id === flowId
                            ? { background: "rgba(228,78,78,.12)", boxShadow: "0 0 0 1.5px #D32F2F" }
                            : { background: "rgba(255,255,255,.04)", boxShadow: "0 0 0 1px rgba(255,255,255,.07)" }}>
                          <div className="text-[13px] font-semibold" style={{ color: f.id === flowId ? "#FAF8F5" : "#C9C0B6" }}>
                            {f.tag === "branch" && <span className="text-[#E44E4E] mr-1">⚠</span>}
                            {f.tag === "proposed" && <span className="text-[#5AA9C9] mr-1">◇</span>}
                            {f.label}
                          </div>
                          <div className="text-[10.5px] text-[#8C8073] mt-0.5">{f.sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* actor rail */}
            <div className="mt-8 rounded-2xl px-4 sm:px-7 py-6 overflow-x-auto"
                 style={{ background: "rgba(255,255,255,.028)", boxShadow: "0 0 0 1px rgba(255,255,255,.07)" }}>
              <div className="flex items-start justify-between gap-3 min-w-[620px]">
                {Object.keys(ACTORS).map((id) => (
                  <ActorNode key={id} id={id} active={activeActors.has(id)} dim={!activeActors.has(id)} />
                ))}
              </div>
            </div>

            {/* stage */}
            <div className="mt-6 grid lg:grid-cols-[1fr_360px] gap-6">
              <div className="rounded-2xl p-6 sm:p-7" style={{ background: "rgba(255,255,255,.035)", boxShadow: "0 0 0 1px rgba(255,255,255,.08)" }}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-[11px] px-2 py-1 rounded" style={{ background: "rgba(228,78,78,.14)", color: "#E44E4E" }}>{step.t}</span>
                  <span className="text-[10.5px] uppercase tracking-[0.18em] text-[#8C8073]">step {cur + 1} of {flow.steps.length}</span>
                </div>
                <h2 className="font-heading text-lg md:text-lg font-bold tracking-tight text-[#FAF8F5]" data-testid="step-title">{step.title}</h2>
                {step.desc && <p className="text-[13.5px] leading-relaxed text-[#B5AAA0] mt-2 max-w-[62ch]">{step.desc}</p>}

                {step.gates && (
                  <div className="mt-5 space-y-2">
                    {step.gates.map((g, idx) => (
                      <div key={g.label} className="flex items-start gap-2.5 rounded-lg px-3 py-2 animate-[fade-up_.45s_cubic-bezier(.16,1,.3,1)_forwards] opacity-0"
                           style={{ background: g.ok ? "rgba(63,164,106,.08)" : "rgba(211,47,47,.12)", animationDelay: `${idx * 70}ms` }}>
                        {g.ok
                          ? <Check className="h-3.5 w-3.5 mt-[3px] shrink-0" style={{ color: "#3FA46A" }} strokeWidth={2.5} />
                          : <X className="h-3.5 w-3.5 mt-[3px] shrink-0" style={{ color: "#E44E4E" }} strokeWidth={2.5} />}
                        <span className="text-[12.5px] leading-snug text-[#DED6CC]">{g.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                {msgs.length > 0 && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8C8073]">What each person actually receives</div>
                      <button data-testid="lang-toggle-btn" onClick={() => setLang(lang === "es" ? "en" : "es")}
                        className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold rounded-full px-2.5 py-1 transition-colors"
                        style={{ background: "rgba(255,255,255,.06)", color: "#C9C0B6" }}>
                        <Languages className="h-3 w-3" />{lang === "es" ? "crew: Español" : "crew: English"}
                      </button>
                    </div>
                    {msgs.map((m, idx) => <Bubble key={idx} m={m} lang={lang} />)}
                  </div>
                )}

                {step.note && (
                  <div className="mt-6 rounded-xl px-4 py-3.5" style={{ background: "rgba(228,78,78,.07)", boxShadow: "inset 2px 0 0 #D32F2F" }}>
                    <div className="text-[9.5px] font-bold uppercase tracking-[0.2em] text-[#E44E4E] mb-1.5">Why it works this way</div>
                    <p className="text-[12.5px] leading-relaxed text-[#DED6CC]">{step.note}</p>
                  </div>
                )}
              </div>

              {/* right rail: record writes + step list */}
              <div className="space-y-6">
                <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,.035)", boxShadow: "0 0 0 1px rgba(255,255,255,.08)" }}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8C8073] mb-3">Written to the record</div>
                  {(step.writes || []).length === 0 && <div className="text-[12px] text-[#6b635c]">nothing — read only step</div>}
                  <div className="space-y-1.5">
                    {(step.writes || []).map((w, idx) => (
                      <div key={w} className="font-mono text-[10.5px] leading-relaxed text-[#B9AC9B] animate-[fade-up_.4s_ease_forwards] opacity-0"
                           style={{ animationDelay: `${idx * 60}ms` }}>
                        <span className="text-[#E44E4E] mr-1.5">›</span>{w}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-2xl p-5" style={{ background: "rgba(255,255,255,.035)", boxShadow: "0 0 0 1px rgba(255,255,255,.08)" }}>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8C8073] mb-3">{flow.label}</div>
                  <div className="space-y-1">
                    {flow.steps.map((s, idx) => (
                      <button key={s.title} data-testid={`step-jump-${idx}`} onClick={() => { setI(idx); setPlaying(false); }}
                        className="w-full text-left rounded-lg px-2.5 py-2 transition-colors flex gap-2.5 items-start"
                        style={{ background: idx === cur ? "rgba(228,78,78,.12)" : "transparent" }}>
                        <span className="font-mono text-[9.5px] mt-[3px] shrink-0 w-[52px]" style={{ color: idx === cur ? "#E44E4E" : "#6b635c" }}>{s.t}</span>
                        <span className="text-[12px] leading-snug" style={{ color: idx === cur ? "#FAF8F5" : "#8C8073" }}>{s.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* controls */}
            <div className="mt-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <button data-testid="prev-step-btn" disabled={cur === 0} onClick={() => { setI(cur - 1); setPlaying(false); }}
                  className="h-10 w-10 rounded-full grid place-items-center transition-colors disabled:opacity-30"
                  style={{ background: "rgba(255,255,255,.06)" }}>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button data-testid="play-btn" onClick={() => setPlaying(!playing)}
                  className="h-10 rounded-full px-5 inline-flex items-center gap-2 text-[12.5px] font-semibold transition-colors"
                  style={{ background: playing ? "rgba(255,255,255,.09)" : "#D32F2F", color: "#fff" }}>
                  {playing ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Play the flow</>}
                </button>
                <button data-testid="next-step-btn" disabled={last} onClick={() => { setI(cur + 1); setPlaying(false); }}
                  className="h-10 w-10 rounded-full grid place-items-center transition-colors disabled:opacity-30"
                  style={{ background: "rgba(255,255,255,.06)" }}>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 h-[3px] rounded-full overflow-hidden max-w-[420px]" style={{ background: "rgba(255,255,255,.08)" }}>
                <div className="h-full transition-all duration-500 rounded-full"
                     style={{ width: `${((cur + 1) / flow.steps.length) * 100}%`, background: "#D32F2F" }} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
